import type { MouseEventHandler } from "react";
import {
  ColorSwatchGroup,
  type ColorSwatchGroupProps,
} from "../../elements/color-swatch/color-swatch";
import { withCloudinaryFourThreePadding } from "../../elements/image/cloudinary";
import { Image, type ImageProps } from "../../elements/image/image";
import { Text } from "../../elements/text/text";
import { cn } from "../../utils/cn";

export interface ProductCardB2BBadge {
  text: string;
  variant: "outline" | "solid";
}

export interface ProductCardB2BPrice {
  /** Omit to hide the dealer price row entirely (e.g. "Hide Dealer Prices" toggle) — RRP still renders. */
  dealerPrice?: string;
  dealerStrikePrice?: string;
  rrpLabel?: string;
  rrpValue?: string;
}

export interface ProductCardB2BProps {
  badges?: ProductCardB2BBadge[];
  image: ImageProps;
  colorSwatches?: ColorSwatchGroupProps;
  price: ProductCardB2BPrice;
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  microcopies: {
    name: string;
    productCode: string;
  };
}

export function ProductCardB2B(props: Readonly<ProductCardB2BProps>) {
  const { badges, colorSwatches, href, image, microcopies, price } = props;

  return (
    <article
      className="group relative flex h-full flex-col gap-4 rounded-lg border border-zinc-200 bg-white p-5 transition-shadow duration-200 ease-out hover:shadow-md has-[:focus-visible]:shadow-md motion-reduce:transition-none"
      id={microcopies.productCode}
    >
      {href && (
        <a
          aria-label={microcopies.name}
          className="absolute inset-0 z-10 rounded-lg focus-visible:outline-2 focus-visible:outline-black focus-visible:outline-offset-2"
          href={href}
          onClick={props.onClick}
        >
          <span className="sr-only">{microcopies.name}</span>
        </a>
      )}

      {/* Badges and image */}
      <div className="flex flex-col gap-1">
        <div className="flex min-h-5 flex-wrap gap-2">
          {badges?.map((badge) => (
            <div
              className={cn(
                "inline-flex items-center rounded-md border px-2.5 py-0.5",
                badge.variant === "solid" &&
                  "border-zinc-900 bg-zinc-900 text-white",
                badge.variant === "outline" &&
                  "border-zinc-200 bg-white text-zinc-900"
              )}
              key={badge.text}
            >
              <Text variant="body-xs-bold">{badge.text}</Text>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-md">
          <Image
            alt={image.alt}
            aspectRatio="4:3"
            fill
            objectFit="contain"
            sizes={image.sizes}
            src={withCloudinaryFourThreePadding(image.src)}
          />
        </div>
      </div>

      {/* Color swatches */}
      {colorSwatches && (
        <div className="pointer-events-none relative z-20 [&_button]:pointer-events-auto">
          <ColorSwatchGroup {...colorSwatches} appearance="b2b" />
        </div>
      )}

      {/* Product info */}
      <div className="flex flex-1 flex-col gap-2">
        <Text variant="body-lg">{microcopies.name}</Text>
        <Text variant="body-sm">{microcopies.productCode}</Text>

        <div className="mt-auto flex flex-col gap-1 pt-2">
          {price.dealerPrice && (
            <div className="flex items-baseline gap-1">
              {price.dealerStrikePrice && (
                <Text variant="body-md-line-through">
                  {price.dealerStrikePrice}
                </Text>
              )}
              <Text variant="body-md-bold">{price.dealerPrice}</Text>
            </div>
          )}
          {price.rrpLabel && price.rrpValue && (
            <div className="flex items-baseline gap-1">
              <Text color="muted" variant="body-sm-bold">
                {price.rrpLabel}
              </Text>
              <Text color="muted" variant="body-sm-bold">
                {price.rrpValue}
              </Text>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
